// The public Policies page. Bracketed items are open decisions, shown as
// placeholders until they are made.

import type { ComponentChildren } from "preact";
import { PageHeader, StatusBadge } from "../../../../ui/src/index.ts";
import { PublicFrame } from "./Landing.tsx";

/** An open decision, shown visibly as a placeholder. */
const Open = ({ children }: { children: string }) => <span class="cs-open"><StatusBadge tone="neutral">{children}</StatusBadge></span>;

const Section = ({ title, children }: { title: string; children: ComponentChildren }) => (
  <section class="cs-article__section"><h2>{title}</h2>{children}</section>
);

export function Policies() {
  return (
    <PublicFrame links footer>
      <article class="cs-public__inner cs-article">
        <div class="cs-article__head">
          <p class="cs-label">Policies</p>
          <PageHeader title={<>Your questions, your answers, <strong>your money</strong></>} />
          <p>This page covers the WarmHub Console. It adds to the <a href="https://warmhub.ai/tos/">Terms of Service</a> and the <a href="https://warmhub.ai/privacy/">Privacy Policy</a>, and doesn't replace them.</p>
        </div>
        <Section title="What we keep about a request">
          <p>For each answer, WarmHub keeps your question, the hub that answered, the intent and tool version, the ask, your bid, the price, the response, and whether you rejected it. It also keeps one event for each hub that a question matched, with how long each step took. We keep these records for <Open>[RETENTION PERIOD]</Open>.</p>
          <p>Answer records and events are about how hubs perform. They are never written into a WarmHub repository.</p>
        </Section>
        <Section title="Who sees your questions">
          <ul>
            <li>The hub's curator sees the questions that were dispatched to their hub, and the questions that matched it but weren't dispatched, so they can improve what the hub covers. <Open>[Whether the curator sees which account asked: DECISION]</Open></li>
            <li>Other consumers never see your questions.</li>
            <li>We don't sell your information.</li>
          </ul>
        </Section>
        <Section title="Where your question goes while we answer it">
          <p>To find the right hubs and understand your question, WarmHub sends it to search and model providers. Each provider may use it only to serve WarmHub. See the subprocessors list in the <a href="https://warmhub.ai/privacy/">Privacy Policy</a>: <Open>[SUBPROCESSORS]</Open>.</p>
        </Section>
        <Section title="Credits and refunds">
          <p>Credits are US dollars in your account. You pay a hub's ask for an answer, never more than your bid. Only answers are charged. If an answer is wrong, reject it and you're credited its full price at once. <Open>[LEGAL REVIEW: the Terms of Service say payments are non-refundable and no credits are issued]</Open></p>
        </Section>
        <Section title="Questions">
          <p>Privacy: <a href="mailto:privacy@warmhub.ai">privacy@warmhub.ai</a>. Billing and support: <a href="mailto:support@warmhub.ai">support@warmhub.ai</a>. Legal: <a href="mailto:legal@warmhub.ai">legal@warmhub.ai</a>. Or <a href="https://warmhub.ai/contact/">get in touch</a>.</p>
        </Section>
      </article>
    </PublicFrame>
  );
}
